import React, { useState, useEffect, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import classNames from 'classnames';
import { ModalHOC } from 'common';
import { useHistory } from 'react-router-dom';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { FormattedMessage } from 'react-intl';
import axios from 'axios';

import { getUserAccounts } from 'backoffice/users/_redux';

import {
  Collapsible,
  NoItemsPanel,
  BaseSelectField,
  CalendarWithLabel,
  StatusCell,
  ActionsButton,
  TableLoader,
  DateCell,
  ReactDataTable,
} from 'common/components';
import { BasketIcon, EditIcon } from 'common/icons';
import noPaymentsImage from 'common/images/noPayments.svg';

import { withTheme } from 'common/styling/theme';
import {
  getOpenOrders,
  getClosedOrders,
  getDelayedOrders,
  getDeferredOrders,
  removeOrder,
} from '../../payments/_redux';

import { staticStyles, getDynamicStyles } from './style';

const startDate = moment().add(-6, 'day').startOf('day').unix();

const endDate = moment().add(1, 'day').startOf('day').unix();

const startCalendar = moment().add(-6, 'day').startOf('day');

const endCalendar = moment().add(1, 'day').startOf('day');

// const getFixedNumber = (num, decimal) => Number(Number(num).toFixed(decimal));

const UserTradingHistory = ({
  accounts,
  accountsAreLoaded,
  getUserAccounts,
  getOpenOrders,
  getClosedOrders,
  getDelayedOrders,
  getDeferredOrders,
  removeOrder,
  orders,
  ordersAreLoaded,
  userId,
  accountId,
  accountLabel,
  theme,
  opened,
  addons,
  profile,
  showModal,
}) => {
  const dynamicStyles = Object.keys(theme).length ? getDynamicStyles(theme) : ` `;

  const history = useHistory();
  const [selectedAccount, setSelectedAccount] = useState({ value: accountId, label: accountLabel });
  const selectedAccountRef = useRef(selectedAccount);
  const [openTradesData, setOpenTradesData] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(0);
  const isInitialMount = useRef(true);

  const isMt5Account = accounts.find(account => account.id === selectedAccount.value)?.platformSlug === 'mt5' ?? false;
  const isCfhAccount = accounts.find(account => account.id === selectedAccount.value)?.platformSlug === 'cfh' ?? false;
  const isBOAccount = accounts.find(account => account.id === selectedAccount.value)?.platformSlug === 'bo' ?? false;

  const openOrdersSupported = !isCfhAccount;
  const [activeTab, setActiveTab] = useState('closed');
  // open orders不显示时间筛选框
  const showCalendar = isMt5Account && activeTab !== 'opened';

  const memoizedOnChangeParams = useMemo(
    () => ({
      clientId: userId,
      accountId: selectedAccount.value,
      activeTab,
    }),
    [userId, accountId, activeTab]
  );

  useEffect(() => {
    getUserAccounts({ userId, noDetails: true });
  }, [getUserAccounts, userId]);

  useEffect(() => {
    if (accounts.length > 0 && accountsAreLoaded && isInitialMount.current) {
      isInitialMount.current = false;
      getClosedOrders({ userId, accountId: selectedAccount.value, start: startDate, end: endDate });
    }
  }, [accounts, accountsAreLoaded]);

  // Open Orders页面定时刷新
  const errorCountRef = useRef(0);
  useEffect(() => {
    // 进入Open Orders页面时重置错误计�?
    errorCountRef.current = 0;

    if (isMt5Account && activeTab === 'opened') {
      const fetchOpenTrades = async () => {
        // 检查是否超�?次错�?
        if (errorCountRef.current >= 5) {
          return;
        }

        try {
          const response = await axios.get(`/backoffice/user/${userId}/account/${selectedAccount.value}/open_trades/`);
          if (response.status === 400 || !response.data || !response.data.trades) {
            errorCountRef.current += 1;
            return;
          }

          errorCountRef.current = 0;
          setOpenTradesData(response.data);
          setLastUpdate(Date.now());
        } catch (error) {
          errorCountRef.current += 1;
        }
      };

      // 首次加载
      fetchOpenTrades();

      // �?秒刷�?
      const intervalId = setInterval(() => {
        if (errorCountRef.current < 5) {
          fetchOpenTrades();
        } else {
          clearInterval(intervalId);
        }
      }, 5000);

      // 清理定时�?
      return () => clearInterval(intervalId);
    }
  }, [isMt5Account, activeTab, userId, selectedAccount.value]);

  const handleOpenCollapse = () => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    if (isMt5Account || isCfhAccount) {
      getClosedOrders({ userId, accountId: selectedAccount.value, start: startDate, end: endDate });
    } else {
      getOpenOrders({ userId, accountId: selectedAccount.value, start: startDate, end: endDate });
    }
  };

  const editOrder = (e, orderId) => {
    e.stopPropagation();
    history.push(`/backoffice/users/${userId}/accounts/${selectedAccount.value}/${orderId}`);
  };

  const deleteOrder = (e, orderId) => {
    e.stopPropagation();
    showModal({
      caption: { id: 'confirmText' },
      content: {
        id: 'confirmDeleteOrder',
      },
      actionButton: {
        textId: 'justYes',
        handler: () => removeOrder({ userId, accountId: selectedAccount.value, orderId, activeTab }),
        type: 'error',
      },
      cancelButton: {
        textId: 'justNo',
      },
    });
  };
  /* eslint-enable */

  const CellWEmpty = ({ value }) => (value || value === 0 ? <span>{value}</span> : <span>一</span>);

  CellWEmpty.propTypes = {
    value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  };

  CellWEmpty.defaultProps = {
    value: '',
  };

  const DateWEmpty = ({ value }) => (value ? <DateCell value={value} /> : <span>一</span>);

  DateWEmpty.propTypes = {
    value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  };

  DateWEmpty.defaultProps = {
    value: '',
  };
  /* eslint-disable */
  const order = {
    Header: 'justOrder',
    Cell: ({
      row: {
        original: { id },
      },
    }) => <CellWEmpty value={id} />,
    Footer: (
      <span className="summary__order">
        <FormattedMessage id="justTotal">{txt => txt}</FormattedMessage>
      </span>
    ),
    isPreviewCell: true,
  };
  const symbol = {
    Header: 'justSymbol',
    Cell: ({
      row: {
        original: { symbol },
      },
    }) => <CellWEmpty value={symbol} />,
    isPreviewCell: true,
  };
  const status = {
    Header: 'justType',
    Cell: ({
      row: {
        original: { command },
      },
    }) => <StatusCell statusCode={command?.toLowerCase().replace('/', '')} noDot />,
    isPreviewCell: true,
  };
  const openTime = {
    Header: 'openTime',
    Cell: ({
      row: {
        original: { open_time },
      },
    }) => <DateWEmpty value={open_time} />,
  };
  const closeTime = {
    Header: 'justCloseTime',
    Cell: ({
      row: {
        original: { close_time, expiration },
      },
    }) => <DateWEmpty value={close_time || expiration} />,
  };
  const openPrice = {
    Header: 'justOpenPrice',
    Cell: ({
      row: {
        original: { open_price },
      },
    }) => <CellWEmpty value={open_price} />,
  };
  const closePrice = {
    Header: document.location.hostname === 'portal.rock-west.com' ? 'justPrice' : 'justClosePrice',
    Cell: ({
      row: {
        original: { close_price },
      },
    }) => <CellWEmpty value={close_price} />,
  };
  const stoploss = {
    Header: 'S/L',
    Cell: ({
      row: {
        original: { stoploss },
      },
    }) => <CellWEmpty value={stoploss} />,
  };
  const takeprofit = {
    Header: 'T/P',
    Cell: ({
      row: {
        original: { takeprofit },
      },
    }) => <CellWEmpty value={takeprofit} />,
  };
  const lots = {
    Header: 'justLots',
    Cell: ({
      row: {
        original: { lots, command },
      },
    }) => <CellWEmpty value={lots === 0.01 && command === 'In/Out' ? 0 : lots} />,
    Footer: (
      <div className="summary__lots">
        <span className="subtext">
          <FormattedMessage id="justLots">{txt => txt}</FormattedMessage>
        </span>
        <div>
          {activeTab === 'opened' && openTradesData?.summary
            ? openTradesData.summary.lots
            : orders.summary
            ? orders.summary.lots
            : ''}
        </div>
      </div>
    ),
  };

  const commission = {
    Header: 'justCommission',
    Cell: ({
      row: {
        original: { commission },
      },
    }) => <CellWEmpty value={commission} />,
    Footer: (
      <div className="summary__commission">
        <span className="subtext">
          <FormattedMessage id="justCommission">{txt => txt}</FormattedMessage>
        </span>
        <div>
          {activeTab === 'opened' && openTradesData?.summary
            ? openTradesData.summary.commission === 0
              ? '一'
              : openTradesData.summary.commission
            : orders.summary
            ? orders.summary.commission === 0
              ? '一'
              : orders.summary.commission
            : ''}
        </div>
      </div>
    ),
  };
  const swap = {
    Header: 'justSwap',
    Cell: ({
      row: {
        original: { swap },
      },
    }) => <CellWEmpty value={swap} />,
    Footer: (
      <div className="summary__swap">
        <span className="subtext">
          <FormattedMessage id="justSwap">{txt => txt}</FormattedMessage>
        </span>
        <div>
          {activeTab === 'opened' && openTradesData?.summary
            ? openTradesData.summary.swap
            : orders.summary
            ? orders.summary.swap
            : ''}
        </div>
      </div>
    ),
  };
  const profit = {
    Header: 'justProfit',
    Cell: ({
      row: {
        original: { profit },
      },
    }) => (
      <CellWEmpty
        value={
          profit !== null && profit !== undefined ? (
            <span style={{ color: profit > 0 ? '#1890ff' : profit < 0 ? '#ff4d4f' : 'inherit' }}>
              {profit}
            </span>
          ) : null
        }
      />
    ),
    Footer: (
      <div className="summary__profit">
        <span className="subtext">
          <FormattedMessage id="justProfit">{txt => txt}</FormattedMessage>
        </span>
        <div>
          {activeTab === 'opened' && openTradesData?.summary
            ? openTradesData.summary.profit
            : orders.summary
            ? orders.summary.profit
            : ''}
        </div>
      </div>
    ),
  };
  const edit = {
    Header: 'justActions',
    Cell: ({
      row: {
        original: { id },
      },
    }) => (
      <>
        <ActionsButton
          tooltipId={`Edit__tooltip-${id}`}
          tooltipTextId="justEdit"
          edit
          onClickFunc={e => editOrder(e, id)}>
          <EditIcon />
        </ActionsButton>
        {!isBOAccount && (
          <ActionsButton
            tooltipId={`Delete__tooltip-${id}`}
            tooltipTextId="justDelete"
            delete
            onClickFunc={e => deleteOrder(e, id)}>
            <BasketIcon />
          </ActionsButton>
        )}
      </>
    ),
  };
  /* eslint-enable */

  const columns = [
    order,
    symbol,
    status,
    openTime,
    closeTime,
    openPrice,
    closePrice,
    lots,
    stoploss,
    takeprofit,
    commission,
    swap,
    profit,
  ];
  if (addons.includes('editOrderSystem') && profile.perms.tradeEditPerm && !(isMt5Account || isCfhAccount)) {
    columns.push(edit);
  }

  const handleAccountChange = ({ value, label }) => {
    setSelectedAccount({ value, label });
    selectedAccountRef.current = { value, label };
  };

  useEffect(() => {
    if (selectedAccountRef.current.value) {
      switch (activeTab) {
        case 'closed':
          getClosedOrders({ userId, accountId: selectedAccountRef.current.value, start: startDate, end: endDate });
          break;
        case 'pending':
          if (isMt5Account) {
            getDeferredOrders({ userId, accountId: selectedAccountRef.current.value, start: startDate, end: endDate });
          } else {
            getDelayedOrders({ userId, accountId: selectedAccountRef.current.value });
          }
          break;
        default:
          if (isMt5Account || isCfhAccount) {
            setActiveTab('closed');
            getClosedOrders({ userId, accountId: selectedAccountRef.current.value, start: startDate, end: endDate });
          } else getOpenOrders({ userId, accountId: selectedAccountRef.current.value });
      }
    }
  }, [selectedAccountRef.current]);

  const handleChangeTab = activeTab => {
    switch (activeTab) {
      case 'closed':
        getClosedOrders({ userId, accountId: selectedAccount.value, start: startDate, end: endDate });
        break;
      case 'pending':
        if (isMt5Account) {
          getDeferredOrders({ userId, accountId: selectedAccount.value, start: startDate, end: endDate });
        } else {
          getDelayedOrders({ userId, accountId: selectedAccount.value });
        }
        break;
      default:
        // open orders由useEffect中的定时刷新处理，不需要手动请�?
        break;
    }
    setActiveTab(activeTab);
  };

  const handleChangeDate = (userId, accountId, activeTab, startDate, endDate) => {
    switch (activeTab) {
      case 'closed':
        getClosedOrders({
          userId,
          accountId,
          start: moment(startDate).format('x'),
          end: moment(endDate).format('x'),
        });
        break;
      case 'pending':
        if (isMt5Account) {
          getDeferredOrders({
            userId,
            accountId,
            start: moment(startDate).format('x'),
            end: moment(endDate).format('x'),
          });
        } else {
          getDelayedOrders({
            userId,
            accountId,
            start: moment(startDate).format('x'),
            end: moment(endDate).format('x'),
          });
        }
        break;
      default:
        // open orders不需要时间参�?
        getOpenOrders({
          userId,
          accountId,
        });
    }
  };

  useEffect(() => {
    setSelectedAccount(prev => (prev.value ? { ...prev } : { value: accounts[0]?.id, label: accounts[0]?.login }));
  }, [accounts, accountsAreLoaded]);

  return (
    <div className="UserTradingHistory">
      {!!accounts.length && accountsAreLoaded && (
        <Collapsible captionId="justTradingHistory" handleOpen={handleOpenCollapse} opened={opened}>
          <div className="UserTradingHistory__select">
            <BaseSelectField
              value={selectedAccount}
              name="userAccount"
              options={accounts.map(account => ({ value: account.id, label: account.login }))}
              onChange={value => handleAccountChange(value)}
              textId="selectAccount"
              disabled={!accountsAreLoaded}
            />
          </div>
          <div className="UserTradingHistory__heading">
            <div className="UserTradingHistory__tabs">
              <FormattedMessage id="justDealOrders">
                {txt => (
                  <button
                    type="button"
                    className={classNames('UserTradingHistory__tab', {
                      active: activeTab === 'closed',
                    })}
                    onClick={() => handleChangeTab('closed')}>
                    {txt}
                  </button>
                )}
              </FormattedMessage>
              {openOrdersSupported && (
                <FormattedMessage id="justOpenOrders">
                  {txt => (
                    <button
                      type="button"
                      className={classNames('UserTradingHistory__tab', {
                        active: activeTab === 'opened',
                      })}
                      onClick={() => handleChangeTab('opened')}>
                      {txt}
                    </button>
                  )}
                </FormattedMessage>
              )}
            </div>
            {showCalendar && (
              <CalendarWithLabel
                label={<FormattedMessage id="justCloseTime">{txt => txt}</FormattedMessage>}
                onChangeFunc={handleChangeDate}
                onChangeParams={memoizedOnChangeParams}
                defaultStartDate={startCalendar}
                defaultEndDate={endCalendar}
              />
            )}
          </div>
          {!ordersAreLoaded && !openTradesData && <TableLoader />}
          {(ordersAreLoaded || openTradesData) && (
            <div>
              {activeTab === 'opened' && openTradesData ? (
                openTradesData.trades?.length ? (
                  <ReactDataTable
                    columns={columns}
                    data={openTradesData.trades}
                    onRowClick={() => {}}
                    isResponsive
                    isInfinite
                    autoResetPage={false}
                    autoResetSelectedRows={false}
                    autoResetSortBy={false}
                    lastUpdate={lastUpdate}
                  />
                ) : (
                  <NoItemsPanel icon={noPaymentsImage} captionSlug="noData" />
                )
              ) : orders?.trades?.length || (Array.isArray(orders) && orders.length) ? (
                <ReactDataTable columns={columns} data={orders?.trades} onRowClick={() => {}} isResponsive isInfinite />
              ) : (
                <NoItemsPanel icon={noPaymentsImage} captionSlug="noData" />
              )}
            </div>
          )}
        </Collapsible>
      )}

      {!accounts.length && accountsAreLoaded && (
        <Collapsible captionId="justTradingHistory" handleOpen={() => {}} opened={opened}>
          <NoItemsPanel icon={noPaymentsImage} captionSlug="justNoOrders" />
        </Collapsible>
      )}
      <style jsx>{staticStyles}</style>
      <style jsx>{dynamicStyles}</style>
    </div>
  );
};

UserTradingHistory.propTypes = {
  accounts: PropTypes.array.isRequired,
  accountsAreLoaded: PropTypes.bool.isRequired,
  getUserAccounts: PropTypes.func.isRequired,
  getOpenOrders: PropTypes.func.isRequired,
  getClosedOrders: PropTypes.func.isRequired,
  getDelayedOrders: PropTypes.func.isRequired,
  getDeferredOrders: PropTypes.func.isRequired,
  removeOrder: PropTypes.func.isRequired,
  orders: PropTypes.object.isRequired,
  ordersAreLoaded: PropTypes.bool.isRequired,
  accountId: PropTypes.number.isRequired,
  accountLabel: PropTypes.string.isRequired,
  userId: PropTypes.number.isRequired,
  profile: PropTypes.object.isRequired,
  showModal: PropTypes.func.isRequired,
  opened: PropTypes.bool,
  theme: PropTypes.object,
  addons: PropTypes.array,
};

UserTradingHistory.defaultProps = {
  opened: false,
  theme: {},
  addons: [],
};
export default compose(
  ModalHOC,
  withTheme(),
  connect(
    state => ({
      accounts: state.backoffice.users.accounts.filter(account => !account.isArchived),
      accountsAreLoaded: state.backoffice.users.accountsAreLoaded,
      orders: state.backoffice.payments.orders,
      ordersAreLoaded: state.backoffice.payments.ordersAreLoaded,
      addons: state.interfaceConfig.addons,
      profile: state.user.profile,
    }),
    {
      getUserAccounts: data => getUserAccounts.request(data),
      getOpenOrders: (userId, accountId) => getOpenOrders.request(userId, accountId),
      getClosedOrders: data => getClosedOrders.request(data),
      getDelayedOrders: data => getDelayedOrders.request(data),
      getDeferredOrders: data => getDeferredOrders.request(data),
      removeOrder: data => removeOrder.request(data),
    }
  )
)(UserTradingHistory);
export { UserTradingHistory };
